// Two-column review screen: Tim's raw answers (left) + editable AI draft (right).
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  adminGetSessionFull,
  adminUpdateDraft,
  adminPublishDraft,
  adminRejectDraft,
} from "@/lib/interview.functions";
import { aiRegenerateFaqAnswer, aiGenerateInterviewDraft } from "@/lib/interview-ai.functions";
import { adminListAllCategories } from "@/lib/blog.functions";
import { listMediaAssets, type MediaAsset } from "@/lib/media.functions";
import {
  ArrowLeft,
  RefreshCw,
  Trash2,
  Save,
  Send,
  ImageIcon,
  X,
  ArrowUp,
  ArrowDown,
  Plus,
  AlertTriangle,
} from "lucide-react";
import { parseYouTubeId, youtubeEmbedUrl, youtubeThumbnailUrl } from "@/lib/youtube";
import {
  attributionLine,
  TEAM_NAME,
  CANONICAL_PERSON_IDS,
  type Credit,
} from "@/lib/attribution";

export const Route = createFileRoute("/_authenticated/interview-review/$sessionId")({
  component: ReviewPage,
  head: () => ({
    meta: [
      { title: "Review Interview Post" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

type FaqItem = { question: string; answer: string };
type Section = { heading: string; body: string; quote?: string | null };

/** Rough overlap check so FAQs don't just repeat the narrative body. */
function overlapsBody(text: string, bodyText: string): boolean {
  const words = text.toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 4);
  if (words.length < 4) return false;
  const body = bodyText.toLowerCase();
  const hits = words.filter((w) => body.includes(w)).length;
  return hits / words.length > 0.85;
}

function CreditEditor({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: Credit[];
  onChange: (c: Credit[]) => void;
}) {
  const quickAdd = [...Object.keys(CANONICAL_PERSON_IDS), TEAM_NAME];
  return (
    <div className="rounded-lg border border-border bg-white p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      {hint ? <p className="mt-1 text-[11px] text-slate-500">{hint}</p> : null}
      <div className="mt-2 space-y-2">
        {value.map((c, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              value={c.name}
              onChange={(e) => {
                const next = value.slice();
                next[i] = { ...c, name: e.target.value };
                onChange(next);
              }}
              placeholder="Name"
              className="flex-1 rounded border border-slate-200 px-2 py-1 text-sm"
            />
            <input
              value={c.role ?? ""}
              onChange={(e) => {
                const next = value.slice();
                next[i] = { ...c, role: e.target.value };
                onChange(next);
              }}
              placeholder="Role (optional)"
              className="flex-1 rounded border border-slate-200 px-2 py-1 text-sm"
            />
            <button
              onClick={() => onChange(value.filter((_, k) => k !== i))}
              className="rounded p-1 text-red-500 hover:bg-red-50"
              aria-label={`Remove ${c.name || "credit"}`}
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1">
        {quickAdd
          .filter((n) => !value.some((c) => c.name === n))
          .map((n) => (
            <button
              key={n}
              onClick={() => onChange([...value, { name: n, role: n === TEAM_NAME ? null : "Owner" }])}
              className="rounded-full border border-border px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-50"
            >
              + {n}
            </button>
          ))}
        <button
          onClick={() => onChange([...value, { name: "", role: "" }])}
          className="rounded-full border border-dashed border-border px-2 py-0.5 text-[11px] text-slate-600"
        >
          <Plus className="mr-0.5 inline h-3 w-3" />
          Other
        </button>
      </div>
    </div>
  );
}

function ReviewPage() {
  const { sessionId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const getFn = useServerFn(adminGetSessionFull);
  const updateFn = useServerFn(adminUpdateDraft);
  const publishFn = useServerFn(adminPublishDraft);
  const rejectFn = useServerFn(adminRejectDraft);
  const regenFn = useServerFn(aiRegenerateFaqAnswer);
  const genDraftFn = useServerFn(aiGenerateInterviewDraft);
  const catFn = useServerFn(adminListAllCategories);

  const q = useQuery({
    queryKey: ["interview-session", sessionId],
    queryFn: () => getFn({ data: { id: sessionId } }),
  });
  const cats = useQuery({ queryKey: ["admin-cats"], queryFn: () => catFn() });

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [intro, setIntro] = useState("");
  const [faq, setFaq] = useState<FaqItem[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [authors, setAuthors] = useState<Credit[]>([]);
  const [contributors, setContributors] = useState<Credit[]>([]);
  const [reviewers, setReviewers] = useState<Credit[]>([]);
  const [categoryId, setCategoryId] = useState<string>("");
  const [featuredImageId, setFeaturedImageId] = useState<string | null>(null);
  const [youtubeUrl, setYoutubeUrl] = useState<string>("");
  const [pickingImage, setPickingImage] = useState(false);
  const [suggestions, setSuggestions] = useState<Record<number, string>>({});
  const [regenLoading, setRegenLoading] = useState<Record<number, boolean>>({});
  const [regenError, setRegenError] = useState<Record<number, string | null>>({});

  useEffect(() => {
    const d = q.data?.draft;
    if (!d) return;
    setTitle(d.title);
    setSlug(d.slug);
    setMetaTitle(d.meta_title);
    setMetaDescription(d.meta_description);
    setIntro(d.intro_paragraph);
    setFaq(d.faq_items ?? []);
    setSections((d as any).body_sections ?? []);
    setAuthors(((d as any).authors ?? []) as Credit[]);
    setContributors(((d as any).contributors ?? []) as Credit[]);
    setReviewers(((d as any).reviewers ?? []) as Credit[]);
    setCategoryId(d.category_id ?? "");
    setFeaturedImageId(d.featured_image_id ?? null);
    setYoutubeUrl(d.youtube_url ?? "");
  }, [q.data?.draft?.id]);

  const save = useMutation({
    mutationFn: () => {
      if (!q.data?.draft) throw new Error("No draft");
      return updateFn({
        data: {
          id: q.data.draft.id,
          title,
          slug,
          meta_title: metaTitle,
          meta_description: metaDescription,
          intro_paragraph: intro,
          body_sections: sections,
          faq_items: faq,
          authors,
          contributors,
          reviewers,
          featured_image_id: featuredImageId,
          category_id: categoryId || null,
          youtube_url: youtubeUrl.trim() || null,
        },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["interview-session", sessionId] }),
  });

  const publish = useMutation({
    mutationFn: async () => {
      await save.mutateAsync();
      if (!q.data?.draft) throw new Error("No draft");
      return publishFn({ data: { draft_id: q.data.draft.id } });
    },
    onSuccess: (res: any) => {
      alert(`Published! Slug: ${res.slug}`);
      navigate({ to: "/admin", search: { module: "interviews" } as any });
    },
  });

  const reject = useMutation({
    mutationFn: () => {
      if (!q.data?.draft) throw new Error("No draft");
      return rejectFn({ data: { draft_id: q.data.draft.id } });
    },
    onSuccess: () => navigate({ to: "/admin", search: { module: "interviews" } as any }),
  });

  const answersByQid = useMemo(() => {
    const m: Record<string, { question_text: string; transcript: string }> = {};
    for (const a of q.data?.answers ?? []) {
      m[a.question_id] = { question_text: a.question_text, transcript: a.transcript };
    }
    return m;
  }, [q.data?.answers]);

  const bodyText = [intro, ...sections.map((x) => `${x.heading} ${x.body}`)].join(" ");

  if (q.isLoading) return <div className="p-10 text-sm text-slate-500">Loading…</div>;
  if (!q.data) return <div className="p-10 text-sm text-slate-500">Not found.</div>;
  const draft = q.data.draft;
  if (!draft) {
    return <NoDraftPanel
      sessionId={sessionId}
      status={q.data.session.status}
      onGenerated={() => qc.invalidateQueries({ queryKey: ["interview-session", sessionId] })}
      genDraftFn={genDraftFn}
    />;
  }

  async function regenerateAnswer(i: number, mode: "tighten" | "polish" = "tighten") {
    const item = faq[i];
    const original = Object.values(answersByQid).find(
      (a) => a.question_text === item.question,
    );
    setRegenLoading((s) => ({ ...s, [i]: true }));
    setRegenError((s) => ({ ...s, [i]: null }));
    try {
      const res: any = await regenFn({
        data: {
          question: item.question,
          current_answer: item.answer,
          transcript: original?.transcript ?? "",
          topic: q.data?.session.topic,
          body_text: bodyText,
          mode,
        },
      });
      if (res?.ok && res.answer) {
        const norm = (s: string) => s.replace(/\s+/g, " ").trim().toLowerCase();
        if (norm(res.answer) === norm(item.answer)) {
          setSuggestions((s) => {
            const { [i]: _drop, ...rest } = s;
            return rest;
          });
          setRegenError((s) => ({
            ...s,
            [i]:
              mode === "polish"
                ? "No changes suggested — the answer already reads clean. Try “Tighten answer” to shorten it."
                : "The AI returned the same wording. Edit the answer slightly and try again.",
          }));
        } else {
          setSuggestions((s) => ({ ...s, [i]: res.answer }));
        }
      } else {
        setRegenError((s) => ({ ...s, [i]: res?.error ?? "Could not regenerate" }));
      }
    } catch (err) {
      setRegenError((s) => ({
        ...s,
        [i]: err instanceof Error ? err.message : String(err),
      }));
    } finally {
      setRegenLoading((s) => ({ ...s, [i]: false }));
    }
  }


  function acceptSuggestion(i: number) {
    const suggestion = suggestions[i];
    if (suggestion == null) return;
    const next = faq.slice();
    next[i] = { ...faq[i], answer: suggestion };
    setFaq(next);
    setSuggestions((s) => {
      const { [i]: _drop, ...rest } = s;
      return rest;
    });
  }

  function rejectSuggestion(i: number) {
    setSuggestions((s) => {
      const { [i]: _drop, ...rest } = s;
      return rest;
    });
    setRegenError((s) => ({ ...s, [i]: null }));
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-border bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
          <Link
            to="/admin"
            search={{ module: "interviews" } as any}
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Interview Posts
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm"
            >
              <Save className="h-4 w-4" /> {save.isPending ? "Saving…" : "Save draft"}
            </button>
            <button
              onClick={() => {
                if (confirm("Reject and delete this draft?")) reject.mutate();
              }}
              className="inline-flex items-center gap-2 rounded-md border border-red-200 px-3 py-1.5 text-sm text-red-700"
            >
              <Trash2 className="h-4 w-4" /> Reject
            </button>
            {(() => {
              const isPublished = q.data.session.status === "published";
              return (
                <button
                  onClick={() => {
                    if (isPublished) return;
                    if (confirm("Publish this post live on the blog?")) publish.mutate();
                  }}
                  disabled={publish.isPending || isPublished}
                  title={isPublished ? "Already published" : undefined}
                  className="inline-flex items-center gap-2 rounded-md bg-accent px-4 py-1.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-600 disabled:opacity-100"
                >
                  <Send className="h-4 w-4" />
                  {isPublished ? "Published" : publish.isPending ? "Publishing…" : "Publish"}
                </button>
              );
            })()}
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-7xl gap-6 px-6 py-6 lg:grid-cols-2">
        {/* LEFT: raw answers */}
        <div className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Tim's raw answers
          </h2>
          <p className="text-xs text-slate-500">Topic: {q.data.session.topic}</p>
          {(q.data.session.questions ?? []).map((qq: any, i: number) => {
            const a = answersByQid[qq.id];
            return (
              <div key={qq.id} className="rounded-lg border border-border bg-white p-4">
                <p className="text-xs font-semibold text-slate-500">Q{i + 1}</p>
                <p className="mt-1 font-medium text-primary">{qq.question_text}</p>
                <p className="mt-3 whitespace-pre-wrap text-sm text-slate-700">
                  {a?.transcript || <span className="italic text-slate-400">No answer</span>}
                </p>
              </div>
            );
          })}
        </div>

        {/* RIGHT: editable draft */}
        <div className="space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            AI-drafted post
          </h2>

          <Field label="Title">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-md border border-border px-3 py-2 text-base font-semibold"
            />
          </Field>

          <Field label="Slug">
            <input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="w-full rounded-md border border-border px-3 py-2 text-sm font-mono"
            />
            <p className="mt-1 text-xs text-slate-500">URL: /blog/{slug}</p>
          </Field>

          <Field label="Category">
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="w-full rounded-md border border-border px-3 py-2 text-sm"
            >
              <option value="">— None —</option>
              {(cats.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label={`Meta title (${metaTitle.length}/60)`}>
            <input
              value={metaTitle}
              onChange={(e) => setMetaTitle(e.target.value)}
              className="w-full rounded-md border border-border px-3 py-2 text-sm"
            />
          </Field>

          <Field label={`Meta description (${metaDescription.length}/160)`}>
            <textarea
              value={metaDescription}
              onChange={(e) => setMetaDescription(e.target.value)}
              rows={2}
              className="w-full rounded-md border border-border px-3 py-2 text-sm"
            />
          </Field>

          <GooglePreview title={metaTitle || title} desc={metaDescription} slug={slug} />

          <Field label="Featured image">
            <FeaturedImagePicker
              imageId={featuredImageId}
              onPick={() => setPickingImage(true)}
              onClear={() => setFeaturedImageId(null)}
            />
          </Field>

          <Field label="YouTube video (optional)">
            <input
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              placeholder="https://www.youtube.com/shorts/… or https://youtu.be/…"
              className="w-full rounded-md border border-border px-3 py-2 text-sm"
            />
            <p className="mt-1 text-xs text-slate-500">
              Paste any YouTube link — watch, Shorts, or youtu.be. Thumbnail and VideoObject schema are generated automatically.
            </p>
            {(() => {
              const vid = parseYouTubeId(youtubeUrl);
              if (!youtubeUrl.trim()) return null;
              if (!vid) {
                return (
                  <p className="mt-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    Not a recognizable YouTube URL.
                  </p>
                );
              }
              return (
                <div className="mt-3 flex items-start gap-3">
                  <div className="relative overflow-hidden rounded-md bg-black" style={{ width: 135, aspectRatio: "9 / 16" }}>
                    <iframe
                      src={youtubeEmbedUrl(vid)}
                      title="Preview"
                      loading="lazy"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      className="absolute inset-0 h-full w-full"
                    />
                  </div>
                  <div className="flex-1 text-xs text-slate-600">
                    <p className="font-mono break-all text-[11px]">ID: {vid}</p>
                    <p className="mt-2">Thumbnail (schema):</p>
                    <a
                      href={youtubeThumbnailUrl(vid)}
                      target="_blank"
                      rel="noreferrer"
                      className="break-all text-accent hover:underline"
                    >
                      {youtubeThumbnailUrl(vid)}
                    </a>
                    <button
                      type="button"
                      onClick={() => setYoutubeUrl("")}
                      className="mt-3 inline-flex items-center gap-1 rounded-md border border-border bg-white px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                    >
                      <X className="h-3 w-3" /> Remove video
                    </button>
                  </div>
                </div>
              );
            })()}
          </Field>


          <Field label="Intro paragraph">
            <textarea
              value={intro}
              onChange={(e) => setIntro(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-border px-3 py-2 text-sm"
            />
          </Field>

          <div className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Attribution
            </h3>
            <CreditEditor
              label="Author(s)"
              hint="Shows in the byline and in Article schema."
              value={authors}
              onChange={setAuthors}
            />
            <CreditEditor
              label="Contributors"
              hint="Interview respondents whose answers shaped the post."
              value={contributors}
              onChange={setContributors}
            />
            <CreditEditor
              label="Reviewed by"
              hint="Optional expert review credit."
              value={reviewers}
              onChange={setReviewers}
            />
            <p className="rounded-md bg-slate-100 px-3 py-2 text-xs text-slate-600">
              Byline preview: {attributionLine(authors, contributors)}
            </p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                Article sections
              </h3>
              <button
                onClick={() => setSections([...sections, { heading: "", body: "", quote: "" }])}
                className="text-xs text-accent hover:underline"
              >
                <Plus className="mr-0.5 inline h-3 w-3" /> Add section
              </button>
            </div>
            {sections.length === 0 ? (
              <p className="rounded-md border border-dashed border-border p-3 text-xs text-slate-500">
                No narrative sections yet. Add sections to publish a full article instead of a
                Q&amp;A-only post.
              </p>
            ) : null}
            {sections.map((sec, i) => (
              <div key={i} className="rounded-lg border border-border bg-white p-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-slate-500">Section {i + 1}</p>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => {
                        if (i === 0) return;
                        const next = sections.slice();
                        [next[i - 1], next[i]] = [next[i], next[i - 1]];
                        setSections(next);
                      }}
                      className="rounded p-1 text-slate-500 hover:bg-slate-100"
                      aria-label="Move section up"
                    >
                      <ArrowUp className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => {
                        if (i === sections.length - 1) return;
                        const next = sections.slice();
                        [next[i], next[i + 1]] = [next[i + 1], next[i]];
                        setSections(next);
                      }}
                      className="rounded p-1 text-slate-500 hover:bg-slate-100"
                      aria-label="Move section down"
                    >
                      <ArrowDown className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => setSections(sections.filter((_, k) => k !== i))}
                      className="rounded p-1 text-red-500 hover:bg-red-50"
                      aria-label="Delete section"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
                <input
                  value={sec.heading}
                  onChange={(e) => {
                    const next = sections.slice();
                    next[i] = { ...sec, heading: e.target.value };
                    setSections(next);
                  }}
                  placeholder="Section heading (H2)"
                  className="mt-2 w-full rounded-md border border-slate-200 px-2 py-1 text-sm font-semibold text-primary"
                />
                <textarea
                  value={sec.body}
                  onChange={(e) => {
                    const next = sections.slice();
                    next[i] = { ...sec, body: e.target.value };
                    setSections(next);
                  }}
                  rows={6}
                  placeholder="Narrative body copy"
                  className="mt-2 w-full rounded-md border border-slate-200 px-2 py-1 text-sm"
                />
                <textarea
                  value={sec.quote ?? ""}
                  onChange={(e) => {
                    const next = sections.slice();
                    next[i] = { ...sec, quote: e.target.value };
                    setSections(next);
                  }}
                  rows={2}
                  placeholder="Optional pull-quote in the respondent's own words"
                  className="mt-2 w-full rounded-md border border-slate-200 px-2 py-1 text-sm italic"
                />
              </div>
            ))}
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              FAQ items
            </h3>
            <p className="text-xs text-slate-500">
              These publish as a separate FAQ block with FAQPage schema — keep them short and
              search-focused, not a copy of the article sections.
            </p>
            {faq.map((f, i) => (
              <div key={i} className="rounded-lg border border-border bg-white p-4">
                {overlapsBody(f.answer, bodyText) ? (
                  <p className="mb-2 flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800">
                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                    This answer closely repeats the article body. Tighten it into a short, direct
                    answer instead.
                  </p>
                ) : null}
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-semibold text-slate-500">Q{i + 1}</p>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => regenerateAnswer(i, "tighten")}
                      disabled={regenLoading[i]}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-accent hover:underline disabled:opacity-50"
                    >
                      <RefreshCw className={`h-3 w-3 ${regenLoading[i] ? "animate-spin" : ""}`} />
                      {regenLoading[i] ? "Rewriting…" : "Tighten answer"}
                    </button>
                    <button
                      onClick={() => regenerateAnswer(i, "polish")}
                      disabled={regenLoading[i]}
                      className="inline-flex items-center gap-1 text-xs text-slate-500 hover:underline disabled:opacity-50"
                    >
                      Light polish
                    </button>
                  </div>
                </div>
                <input
                  value={f.question}
                  onChange={(e) => {
                    const next = faq.slice();
                    next[i] = { ...f, question: e.target.value };
                    setFaq(next);
                  }}
                  className="mt-2 w-full rounded-md border border-slate-200 px-2 py-1 text-sm font-semibold text-primary"
                />
                <textarea
                  value={f.answer}
                  onChange={(e) => {
                    const next = faq.slice();
                    next[i] = { ...f, answer: e.target.value };
                    setFaq(next);
                  }}
                  rows={4}
                  className="mt-2 w-full rounded-md border border-slate-200 px-2 py-1 text-sm"
                />
                {regenError[i] ? (
                  <p className="mt-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    {regenError[i]}
                  </p>
                ) : null}
                {suggestions[i] != null ? (
                  <div className="mt-3 rounded-md border border-accent/40 bg-accent/5 p-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-wide text-accent">
                        ✨ Suggested refinement
                      </p>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => regenerateAnswer(i)}
                          disabled={regenLoading[i]}
                          className="inline-flex items-center gap-1 rounded-md border border-border bg-white px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-50"
                        >
                          <RefreshCw className="h-3 w-3" /> Try again
                        </button>
                        <button
                          onClick={() => rejectSuggestion(i)}
                          className="rounded-md border border-border bg-white px-2 py-1 text-xs hover:bg-slate-50"
                        >
                          Reject
                        </button>
                        <button
                          onClick={() => acceptSuggestion(i)}
                          className="rounded-md bg-accent px-2 py-1 text-xs font-semibold text-white hover:opacity-90"
                        >
                          Accept
                        </button>
                      </div>
                    </div>
                    <div className="mt-2 rounded-md border border-slate-200 bg-white px-2 py-1 text-sm leading-relaxed">
                      <DiffView before={f.answer} after={suggestions[i]} />
                    </div>
                    <textarea
                      value={suggestions[i]}
                      onChange={(e) =>
                        setSuggestions((s) => ({ ...s, [i]: e.target.value }))
                      }
                      rows={4}
                      className="mt-2 w-full rounded-md border border-slate-200 px-2 py-1 text-sm"
                    />
                    <p className="mt-1 text-[11px] text-slate-500">
                      Edit the suggestion above before accepting, or click Accept to replace the current answer.
                    </p>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </div>

      {pickingImage ? (
        <MediaPicker
          onPick={(id) => {
            setFeaturedImageId(id);
            setPickingImage(false);
          }}
          onClose={() => setPickingImage(false)}
        />
      ) : null}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
        {label}
      </span>
      {children}
    </label>
  );
}

function GooglePreview({ title, desc, slug }: { title: string; desc: string; slug: string }) {
  return (
    <div className="rounded-lg border border-border bg-white p-4">
      <p className="text-xs uppercase text-slate-400">Google preview</p>
      <p className="mt-1 text-xs text-emerald-700">durafoaminc.com › blog › {slug}</p>
      <p className="mt-0.5 text-base text-[#1a0dab]">{title || "Untitled"}</p>
      <p className="text-xs text-slate-600">{desc || "No description"}</p>
    </div>
  );
}

function FeaturedImagePicker({
  imageId,
  onPick,
  onClear,
}: {
  imageId: string | null;
  onPick: () => void;
  onClear: () => void;
}) {
  const listFn = useServerFn(listMediaAssets);
  const list = useQuery({
    queryKey: ["media-list-small"],
    queryFn: () => listFn(),
  });
  const asset = (list.data ?? []).find((a: MediaAsset) => a.id === imageId);
  return (
    <div className="flex items-center gap-3">
      {asset ? (
        <>
          <img
            src={asset.webpUrl ?? asset.url}
            alt=""
            className="h-16 w-24 rounded object-cover"
          />
          <button
            onClick={onPick}
            className="rounded-md border border-border px-3 py-1.5 text-sm"
          >
            Change
          </button>
          <button onClick={onClear} className="text-sm text-red-600">
            Remove
          </button>
        </>
      ) : (
        <button
          onClick={onPick}
          className="inline-flex items-center gap-2 rounded-md border border-dashed border-border px-3 py-2 text-sm text-slate-600"
        >
          <ImageIcon className="h-4 w-4" /> Pick from Media Library
        </button>
      )}
    </div>
  );
}

function MediaPicker({
  onPick,
  onClose,
}: {
  onPick: (id: string) => void;
  onClose: () => void;
}) {
  const listFn = useServerFn(listMediaAssets);
  const list = useQuery({
    queryKey: ["media-picker"],
    queryFn: () => listFn(),
  });
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <div className="my-6 w-full max-w-5xl rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h3 className="text-sm font-semibold">Choose an image</h3>
          <button onClick={onClose} className="rounded p-1 hover:bg-slate-100">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto p-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {(list.data ?? []).map((a: MediaAsset) => (
              <button
                key={a.id}
                onClick={() => onPick(a.id)}
                className="group relative aspect-video overflow-hidden rounded-md border border-border"
              >
                <img
                  src={a.webpUrl ?? a.url}
                  alt={a.altText ?? ""}
                  className="h-full w-full object-cover transition group-hover:scale-105"
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function NoDraftPanel({
  sessionId,
  status,
  onGenerated,
  genDraftFn,
}: {
  sessionId: string;
  status: string;
  onGenerated: () => void;
  genDraftFn: (args: { data: { session_id: string } }) => Promise<any>;
}) {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    setRunning(true);
    setError(null);
    try {
      const res: any = await genDraftFn({ data: { session_id: sessionId } });
      if (res?.ok) {
        onGenerated();
      } else {
        setError(res?.error ?? "Draft generation failed");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="p-10">
      <Link to="/admin" className="text-sm text-slate-600">
        ← Admin
      </Link>
      <div className="mt-6 max-w-xl space-y-4">
        <p className="text-slate-700">
          No draft yet. Status: <strong>{status}</strong>
        </p>
        {status === "awaiting_ai_draft" && (
          <p className="text-sm text-slate-600">
            The background draft generation didn't complete. Click below to generate
            the FAQ post now from Tim's answers.
          </p>
        )}
        <button
          onClick={run}
          disabled={running}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white shadow hover:opacity-90 disabled:opacity-50"
        >
          {running ? (
            <>
              <RefreshCw className="h-4 w-4 animate-spin" />
              Generating draft…
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4" />
              Generate Draft Now
            </>
          )}
        </button>
        {error && (
          <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

// Word-level diff: removed words struck-through red, added words green.
function DiffView({ before, after }: { before: string; after: string }) {
  const a = before.split(/(\s+)/);
  const b = after.split(/(\s+)/);
  const n = a.length;
  const m = b.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out: Array<{ t: string; kind: "same" | "add" | "del" }> = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ t: a[i], kind: "same" });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ t: a[i], kind: "del" });
      i++;
    } else {
      out.push({ t: b[j], kind: "add" });
      j++;
    }
  }
  while (i < n) out.push({ t: a[i++], kind: "del" });
  while (j < m) out.push({ t: b[j++], kind: "add" });
  return (
    <span>
      {out.map((seg, idx) => {
        if (seg.kind === "same") return <span key={idx}>{seg.t}</span>;
        if (seg.kind === "add")
          return (
            <span key={idx} className="rounded bg-green-100 text-green-800">
              {seg.t}
            </span>
          );
        return (
          <span key={idx} className="rounded bg-red-100 text-red-800 line-through">
            {seg.t}
          </span>
        );
      })}
    </span>
  );
}


